import {Component, Inject, isDevMode, OnDestroy, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {Observable, Subject, take, takeUntil} from "rxjs";
import {CentreOption} from "../../../../../interfaces/Centres";
import {MatSelectChange} from "@angular/material/select";
import {FullChild} from "../../../../../interfaces/FullChild";
import {ChildService} from "../../../../../components/child/child.service";
import {CentreService} from "../../../../../services/centre.service";
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {CounselingService} from "../../../counseling.service";
import {environment} from "../../../../../../environments/environment";

@Component({
  selector: 'app-add-edit-counselling-centre-log',
  templateUrl: './add-edit-counselling-centre-log.component.html',
  styleUrls: ['./add-edit-counselling-centre-log.component.scss']
})
export class AddEditCounsellingCentreLogComponent implements OnInit, OnDestroy {
  env = isDevMode() ? environment.testMode : environment.productionMode
  centreLogForm!: FormGroup;
  selectedCentre: any;
  docId: any;
  _destroyed$ = new Subject();
  allCenters$!: Observable<CentreOption[]>;
  childData: FullChild[] = [];
  child$?: Observable<FullChild[]>;
  sessions = ["Session 1", "Session 2", "Session 3", "Session 4", "Session 5","Session 6","Session 7","Session 8","Session 9","Session 10"];
  types = [
    "Admission Counselling",
    "Regular Counselling",
    "Follow Up Counselling",
    "Discharge Counselling",
    "Others"
  ];

  // For multiple entries mode
  displayedColumns: string[] = ['child', 'session', 'type', 'date', 'remark', 'actions'];
  tableDataSource: any[] = [];

  displayCentre = (c: any) => c?.centreName;
  isLoading :boolean = false;
  constructor(
    private fb: FormBuilder,
    private childService: ChildService,
    private centreService: CentreService,
    private counsellingService: CounselingService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<AddEditCounsellingCentreLogComponent>,
    private afs: AngularFirestore
  ) {
    this.allCenters$ = this.centreService.getAllCentres();
    this.docId = data?.docId || null;
  }


  ngOnInit(): void {
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    // Edit mode - single entry form
    if (this.data?.mode === 'edit' && this.docId) {
      this.centreLogForm = new FormGroup({
        centreCode: new FormControl('', [Validators.required]),
        centreName: new FormControl('', [Validators.required]),
        childName: new FormControl('', [Validators.required]),
        childCode: new FormControl('', [Validators.required]),
        session: new FormControl('', [Validators.required]),
        type: new FormControl('', [Validators.required]),
        date: new FormControl('', [Validators.required]),
        remark: new FormControl(''),
        sessionDocumented: new FormControl('No'),
        createdAt: new FormControl(new Date()),
        createdBy: new FormControl(user?.email || 'Unknown'),
        updatedAt: new FormControl(null),
        updatedBy: new FormControl(null),
      });

      this.afs.doc(`${this.env.counsellingCenterLog}/${this.docId}`).valueChanges()
        .pipe(takeUntil(this._destroyed$))
        .subscribe((doc: any) => {
          if (doc) {
            this.centreLogForm.patchValue({
              ...doc,
              date: doc.date.toDate(),
            });
          }
        });
    } else {
      // Add mode - multiple entries form
      this.centreLogForm = this.fb.group({
        centreCode: ['', Validators.required],
        centreName: ['', Validators.required],
        entries: this.fb.array([]) // FormArray for multiple entries
      });
    }

  }

  onCentreSelect(event: any) {
    const centre = event;
    this.centreLogForm.patchValue({
      centreName: centre.centreName,
      centreCode: centre.centreCode
    });


    this.child$ = this.childService.getAllotedChildListByCenter(centre.centreCode);

    this.child$
      .pipe(takeUntil(this._destroyed$))
      .subscribe(res => {
        this.childData = res;

        this.childService
          .getDischargeChildsBeforeThreeDaysFromCurrentDateByCentre(centre.centreCode)
          .then(res1 => {
            const discharge = res1.docs.map(x => x.data() as FullChild);
            this.childData.push(...discharge);
          });
      });
  }

  childSelect(event: MatSelectChange) {
    const child = event.value as FullChild;
    this.centreLogForm.patchValue({
      childName: `${child.firstName} ${child.lastName}`,
      childCode: child.childCode
    });
  }

  // Get entries FormArray
  get entries(): FormArray {
    return this.centreLogForm.get('entries') as FormArray;
  }

  // Create a new entry form group
  createEntry(): FormGroup {
    return this.fb.group({
      centreCode: ['', Validators.required],
      centreName: ['', Validators.required],
      childName: ['', Validators.required],
      childCode: ['', Validators.required],
      session: ['', Validators.required],
      type: ['', Validators.required],
      date: ['', Validators.required],
      remark: [''],
      sessionDocumented: ['No']
    });
  }

  // Add a new entry row
  addEntry() {
    console.log('Add Entry Clicked');
    console.log('Children Fetched', this.childData.length);
    this.entries.push(this.createEntry());
    this.updateTableDataSource();
  }

  // Remove an entry row
  removeEntry(index: number) {
    this.entries.removeAt(index);
    this.updateTableDataSource();
  }

  // Update the table data source to trigger change detection
  private updateTableDataSource() {
    this.tableDataSource = [...this.entries.controls];
    console.log('Table data source updated. Entries count:', this.tableDataSource.length);
  }

  // Handle child selection in the table row
  onChildSelectInRow(event: MatSelectChange, index: number) {
    const child = event.value as FullChild;
    const entryGroup = this.entries.at(index);
    entryGroup.patchValue({
      childName: `${child.firstName} ${child.lastName}`,
      childCode: child.childCode,
      centreCode: child.centreCode,
      centreName: child.centreName
    });
  }

  // Get CSS class for counselling type
  getTypeClass(type: string): string {
    const typeMap: { [key: string]: string } = {
      'Admission Counselling': 'type-admission',
      'Regular Counselling': 'type-regular',
      'Follow Up Counselling': 'type-followup',
      'Discharge Counselling': 'type-discharge',
      'Others': 'type-others'
    };
    return typeMap[type] || 'type-others';
  }


  onSubmit() {
    if (this.centreLogForm.invalid) {
      alert('Please fill all required fields');
      return;
    }
    this.isLoading = true;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const formValue = this.centreLogForm.value;

    // EDIT MODE - Single entry update
    if (this.docId) {
      const updateData = {
        ...formValue,
        updatedAt: new Date(),
        updatedBy: user?.email || "Unknown"
      };

      this.afs.doc(`${this.env.counsellingCenterLog}/${this.docId}`)
        .update(updateData)
        .then(() => {
          console.log("Counselling Log Updated:", updateData);
          this.isLoading = false;
          alert("Counselling log updated successfully!");
          this.dialogRef.close();
        })
        .catch(err => {
          console.error("Error updating log:", err);
          alert("Error updating log");
        });
      return;
    }

    // ADD MODE - Multiple entries
    if (!formValue.entries || formValue.entries.length === 0) {
      alert('Please add at least one entry');
      this.isLoading = false;
      return;
    }

    // First, check for matching sessions for all entries
    const sessionCheckPromises = formValue.entries.map((entry: any) => {
      return this.counsellingService.fetchIndividualSession()
        .pipe(take(1))
        .toPromise()
        .then(sessions => {
          const formDate = new Date(entry.date);
          const formDateStr = formDate.toISOString().split('T')[0];
          const matched = sessions?.find(x => {
            const sessionDate = x.date?.toDate ? x.date.toDate() : x.date;
            const sessionDateStr = new Date(sessionDate).toISOString().split('T')[0];
            return (
              x.centreCode === entry.centreCode &&
              x.childCode === entry.childCode &&
              sessionDateStr === formDateStr
            );
          });
          return { entry, matched };
        });
    });

    // Wait for all session checks to complete
    Promise.all(sessionCheckPromises)
      .then(results => {
        // Now create batch with all data including sessionDocumented status
        const batch = this.afs.firestore.batch();
        const sessionUpdatePromises: Promise<any>[] = [];

        results.forEach(({ entry, matched }) => {
          const docRef = this.afs.collection(this.env.counsellingCenterLog).doc().ref;
          const entryData = {
            centreCode: entry.centreCode,
            centreName: entry.centreName,
            childName: entry.childName,
            childCode: entry.childCode,
            session: entry.session,
            type: entry.type,
            date: entry.date,
            remark: entry.remark || '',
            sessionDocumented: matched ? 'Yes' : 'No',
            createdAt: new Date(),
            createdBy: user?.email || 'Unknown',
            updatedAt: null,
            updatedBy: null
          };

          batch.set(docRef, entryData);

          // If matched, queue the session document update (done separately after batch)
          if (matched) {
            const sessionUpdatePromise = this.afs.doc(`${this.env.counseling}/allSessions/sessions/${matched.id}`)
              .update({ sessionDocumented: "Yes" })
              .then(() => console.log(`Session ${matched.id} updated with sessionDocumented=Yes`))
              .catch(err => console.error("Error updating session:", err));
            sessionUpdatePromises.push(sessionUpdatePromise);
          }
        });

        // Commit the batch
        return batch.commit()
          .then(() => {
            // After batch is committed, update the session documents
            return Promise.all(sessionUpdatePromises);
          });
      })
      .then(() => {
        alert(`${formValue.entries.length} counselling log(s) added successfully!`);
        this.isLoading = false;
        this.dialogRef.close();
        this.centreLogForm.reset();
      })
      .catch(err => {
        console.error("Error adding logs:", err);
        this.isLoading = false;
        alert("Error adding logs: " + err.message);
      });
  }


  onCancel() {
    this.centreLogForm.reset();
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this._destroyed$.next('');
    this._destroyed$.complete();
  }
}
